import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { ContentItemViewNodesType } from './types'
import { nullOrUndefinedResolver } from './utils'

interface SetContentItemToPromoteDemote {
    contentItemToPromoteDemote: Partial<TextContentElement>
    isInProject: boolean
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
}
/**
 * The state of the promote/demote functionality.
 */
interface PromoteDemoteState {
    /**
     * The content item to promote or demote.
     */
    contentItemToPromoteDemote: Partial<TextContentElement> | null
    /**
     * Indicates whether the content item is in the project.
     */
    isInProject: boolean
    /**
     * Indicates whether the content item is user added.
     */
    isUserAdded: boolean
    /**
     * Indicates whether the content item is an L1 paragraph.
     */
    isL1Paragraph: boolean
    /**
     * Indicates whether the content item is an article.
     */
    isArticle: boolean
    /**
     * The level of the paragraph (0-5).
     */
    paragraphLevel: number
    /**
     * Determines if the content can be promoted.
     */
    canBePromoted: (isContentTeamFromStudioPage: boolean) => boolean
    /**
     * Determines if the content can be demoted.
     */
    canBeDemoted: (isContentTeamFromStudioPage: boolean) => boolean
    /**
     * Sets the content item to promote or demote.
     */
    setContentItemToPromoteDemote: (
        props: Partial<SetContentItemToPromoteDemote> | null
    ) => void
    /**
     * Resets the promote/demote state.
     */
    resetPromoteDemoteState: () => void
}

interface CalculatePromoteDemoteEligibilityOptions extends PromoteDemoteState {
    isContentTeamFromStudioPage: boolean
}
/**
 * Helper function to calculate promotion and demotion eligibility
 *
 * @param isL1Paragraph Whether the content is an L1 paragraph
 * @param isArticle Whether the content is an article
 * @param paragraphLevel The level of the paragraph (0-5)
 * @param isInProject Whether the content is in the project
 * @param isUserAdded Whether the content is user added
 * @param isContentTeamFromStudioPage Whether the content is from the content team
 * @returns Object containing canBePromoted and canBeDemoted flags
 */
const calculatePromoteDemoteEligibility = (
    options: CalculatePromoteDemoteEligibilityOptions
) => {
    // Calculate promotion eligibility
    // Content cannot be promoted if:
    // 1. It's an L1 paragraph or article
    // 2. It's not from content team AND not in project
    const canBePromoted = !(
        options.isL1Paragraph ||
        options.isArticle ||
        (!options.isContentTeamFromStudioPage &&
            (!options.isInProject || !options.isUserAdded))
    )

    let canBeDemoted = false

    // Special case for L1 paragraphs
    if (
        options.isL1Paragraph &&
        options.paragraphLevel > 0 &&
        (options.isContentTeamFromStudioPage ||
            (!options.isContentTeamFromStudioPage &&
                options.isInProject &&
                options.isUserAdded))
    ) {
        // TODO: determine if possible parent is active (not hidden)
        canBeDemoted = true
    }
    // Regular case - content can be demoted if:
    // 1. Not an L1 paragraph, not an article, and level > 0 and < 6
    // 2. From content team OR in project
    else if (
        !options.isL1Paragraph &&
        !options.isArticle &&
        options.paragraphLevel > 0 &&
        (options.isContentTeamFromStudioPage ||
            (!options.isContentTeamFromStudioPage &&
                options.isInProject &&
                options.isUserAdded))
    ) {
        canBeDemoted = options.paragraphLevel < 6
    }

    return { canBePromoted, canBeDemoted }
}

const defaultState = {
    contentItemToPromoteDemote: null,
    isInProject: false,
    isUserAdded: false,
    isL1Paragraph: false,
    isArticle: false,
    paragraphLevel: 0,
}

export const usePromoteDemoteStore = create<PromoteDemoteState>((set, get) => ({
    ...defaultState,
    canBePromoted: (isContentTeamFromStudioPage: boolean = false) => {
        const options = {
            ...get(),
            isContentTeamFromStudioPage,
        }
        const hasSelectedContentItemToPromoteDemote = Boolean(
            options.contentItemToPromoteDemote
        )
        const { canBePromoted } = calculatePromoteDemoteEligibility(options)

        return hasSelectedContentItemToPromoteDemote && canBePromoted
    },

    canBeDemoted: (isContentTeamFromStudioPage: boolean = false) => {
        const options = {
            ...get(),
            isContentTeamFromStudioPage,
        }
        const hasSelectedContentItemToPromoteDemote = Boolean(
            options.contentItemToPromoteDemote
        )
        const { canBeDemoted } = calculatePromoteDemoteEligibility(options)

        return hasSelectedContentItemToPromoteDemote && canBeDemoted
    },

    resetPromoteDemoteState: () => set(defaultState),

    // Action to update the state based on content analysis
    setContentItemToPromoteDemote: (
        props: Partial<SetContentItemToPromoteDemote> | null
    ) => {
        if (!props || !props.contentItemViewNodes?.length) {
            // reset state if no content item or nodes are provided
            set(defaultState)
            return
        }

        const {
            contentItemViewNodes,
            contentItemToPromoteDemote,
            isInProject,
        } = nullOrUndefinedResolver(props)

        // find the content item
        const content = contentItemViewNodes?.find((node) =>
            node.elements?.some(
                (element) => element.id === contentItemToPromoteDemote.id
            )
        )

        // extract content required properties for evaluation
        const paragraphLevel = content?.order || 0
        const isArticle = content?.textType === 'Article'
        const isUserAdded = content?.isUserAdded || false
        // determine if content is an L1 paragraph
        const isL1Paragraph = Boolean(
            content &&
                contentItemViewNodes?.some((node) => {
                    if (content.dtvContentItemParentId) {
                        return (
                            node.id === content.dtvContentItemParentId &&
                            node.textType === 'Article'
                        )
                    }
                    return (
                        node.dtvItemId === content.dtvParentId &&
                        node.textType === 'Article'
                    )
                })
        )

        // update store state with evaluated properties
        set({
            contentItemToPromoteDemote,
            isInProject,
            isUserAdded,
            isL1Paragraph,
            isArticle,
            paragraphLevel,
        })
    },
}))

/**
 * A custom hook that retrieves a shallowly selected value from the `useHighlightStore` store.
 * Sample usage:
 * ```typescript
 * const { contentItemToPromoteDemote, setContentItemToPromoteDemote } = usePromoteDemoteSelector((state) => ({
 *   contentItemToPromoteDemote: state.contentItemToPromoteDemote,
 *   setContentItemToPromoteDemote: state.setContentItemToPromoteDemote,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const usePromoteDemoteSelector = <T>(
    selector: (state: PromoteDemoteState) => T
): T => usePromoteDemoteStore(useShallow(selector))
